<script lang="ts">
  import { defineComponent, PropType } from 'vue'
  import type { OptionItemI } from '@/typescript/types/ui-components/SelectItem'
  import { CommonErrorI } from '@/typescript/types/abstracts/errors/ErrorHandlerTypes'

  export default defineComponent({
    name: 'SelectItem',
    props: {
      modelValue: { type: [String, Number], default: null },
      name: { type: String, default: '' },
      error: { type: [Object, String] as PropType<CommonErrorI | string | null | undefined>, default: () => ({}) },
      label: { type: String, default: '' },
      placeholder: { type: String, default: '' },
      disabled: Boolean,
      selectOptions: { type: Array as PropType<OptionItemI[]>, default: () => [] },
      isAlwaysShowLabel: { type: Boolean, default: false },
      labelExistButNotVisible: { type: Boolean, default: false },
      isMaxContent: { type: Boolean, default: false },
      zIndex: { type: Number, default: 10 },
      withSearch: { type: Boolean, default: false },
      searchPlaceholder: { type: String, default: 'Search...' },

      // External ARIA hooks (optional, e.g., hint text id)
      ariaLabel: { type: String, default: '' },
      ariaDescribedby: { type: String, default: '' }, // external hint/help id(s)
      ariaLabelledby: { type: String, default: '' }, // external label id
    },
    emits: ['clearErrors', 'update:modelValue', 'change'],
    data() {
      return {
        isOpen: false,
        labelIsVisible: false,
        activeOptionIndex: -1,
        uidFallback: `select-${Math.random().toString(36).slice(2)}`,
        searchQuery: '',
      }
    },
    computed: {
      internalValue: {
        get() {
          return this.modelValue
        },
        set(v: string | number) {
          this.$emit('update:modelValue', v)
        },
      },
      getOptionTitle() {
        if (!this.selectOptions.length) return this.placeholder
        const current = this.selectOptions.find(i => !i.isHeader && i.value === this.modelValue)
        if (!this.modelValue && this.placeholder) return this.placeholder
        return current?.inputTitle ?? current?.title ?? this.placeholder
      },
      filteredOptions(): OptionItemI[] {
        if (!this.withSearch || !this.searchQuery.trim()) return this.selectOptions
        const q = this.searchQuery.toLowerCase()
        const kept = this.selectOptions.filter(
          o => o.isHeader || `${o.title} ${o.rightLabel ?? ''}`.toLowerCase().includes(q),
        )
        // Drop group headings left with no matching option beneath them.
        return kept.filter((o, i) => !o.isHeader || (kept[i + 1] && !kept[i + 1].isHeader))
      },
      /** Options the user can actually land on — group headings are skipped. */
      hasSelectableOption(): boolean {
        return this.filteredOptions.some(o => !o.isHeader)
      },
      firstSelectableIndex(): number {
        return this.filteredOptions.findIndex(o => !o.isHeader)
      },
      lastSelectableIndex(): number {
        for (let i = this.filteredOptions.length - 1; i >= 0; i--) {
          if (!this.filteredOptions[i].isHeader) return i
        }
        return -1
      },

      // IDs
      selectId(): string {
        return this.name || this.uidFallback
      },
      listboxId(): string {
        return `${this.selectId}-listbox`
      },
      labelId(): string | undefined {
        return this.label ? `${this.selectId}-label` : undefined
      },
      errorId(): string {
        return `${this.selectId}-error`
      },

      // States
      hasError(): boolean {
        const e: any = this.error
        return !!(e && (e.hasError || e.message || Object.keys(e).length))
      },

      // ARIA name/description
      labelledById(): string | undefined {
        if (this.ariaLabelledby) return this.ariaLabelledby
        return this.labelId
      },
      ariaLabelComputed(): string | undefined {
        // Only if there’s no labelledby
        if (!this.labelledById) return this.ariaLabel || this.label || this.placeholder || 'Select'
        return undefined
      },
      describedByIds(): string | undefined {
        // Only external hint/help here (NOT the error, since we use aria-errormessage)
        const ids = (this.ariaDescribedby || '').trim()
        return ids ? ids : undefined
      },

      // Styles
      getStyle() {
        return this.hasError ? 'error' : ''
      },
    },
    mounted() {
      if (this.isAlwaysShowLabel) this.labelIsVisible = true
    },
    methods: {
      toggleLabel(val: boolean) {
        if (!this.isAlwaysShowLabel) this.labelIsVisible = val
      },
      onToggleDropdown() {
        if (this.disabled) return
        this.isOpen = !this.isOpen
        if (this.isOpen) {
          const idx = this.filteredOptions.findIndex(o => !o.isHeader && o.value === this.modelValue)
          this.activeOptionIndex = idx >= 0 ? idx : this.firstSelectableIndex
          this.$nextTick(() => {
            if (this.withSearch) {
              const input = this.$el.querySelector('.select-search-input') as HTMLInputElement | null
              input?.focus()
            } else {
              this.scrollToActiveOption()
            }
          })
        } else {
          this.activeOptionIndex = -1
          this.searchQuery = ''
        }
        this.$emit('clearErrors')
      },
      onSelectOption(value: string | number) {
        this.internalValue = value
        this.$emit('change', value)
        this.isOpen = false
        this.activeOptionIndex = -1
      },
      /** Steps the active option by `step`, wrapping around and skipping group headings. */
      moveActiveOption(step: number) {
        const total = this.filteredOptions.length
        if (!total || !this.hasSelectableOption) return
        let next = this.activeOptionIndex
        for (let i = 0; i < total; i++) {
          next = (next + step + total) % total
          if (!this.filteredOptions[next].isHeader) {
            this.activeOptionIndex = next
            break
          }
        }
        this.$nextTick(this.scrollToActiveOption)
      },
      selectActiveOption() {
        const option = this.filteredOptions[this.activeOptionIndex]
        if (option && !option.isHeader) this.onSelectOption(option.value)
      },
      closeDropdown() {
        this.isOpen = false
        this.activeOptionIndex = -1
        this.searchQuery = ''
      },
      scrollToActiveOption() {
        if (this.activeOptionIndex < 0) return
        const opt = document.getElementById(`${this.selectId}-option-${this.activeOptionIndex}`)
        const list = document.getElementById(this.listboxId)
        if (opt && list) {
          const top = opt.offsetTop,
            h = opt.offsetHeight,
            lh = list.offsetHeight,
            s = list.scrollTop
          if (top < s) list.scrollTop = top
          else if (top + h > s + lh) list.scrollTop = top + h - lh
        }
      },
      onKeydown(e: KeyboardEvent) {
        if (this.disabled) return
        switch (e.key) {
          case 'Enter':
          case ' ':
            e.preventDefault()
            if (this.isOpen && this.activeOptionIndex >= 0) {
              this.selectActiveOption()
            } else this.onToggleDropdown()
            break
          case 'Escape':
            if (this.isOpen) {
              e.preventDefault()
              this.closeDropdown()
            }
            break
          case 'ArrowDown':
            e.preventDefault()
            if (!this.isOpen) this.onToggleDropdown()
            else this.moveActiveOption(1)
            break
          case 'ArrowUp':
            e.preventDefault()
            if (this.isOpen) this.moveActiveOption(-1)
            break
          case 'Home':
            if (this.isOpen) {
              e.preventDefault()
              this.activeOptionIndex = this.firstSelectableIndex
              this.$nextTick(this.scrollToActiveOption)
            }
            break
          case 'End':
            if (this.isOpen) {
              e.preventDefault()
              this.activeOptionIndex = this.lastSelectableIndex
              this.$nextTick(this.scrollToActiveOption)
            }
            break
        }
      },
      onOptionHover(i: number) {
        if (this.filteredOptions[i]?.isHeader) return
        this.activeOptionIndex = i
      },
      onSearchKeydown(e: KeyboardEvent) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault()
            this.moveActiveOption(1)
            break
          case 'ArrowUp':
            e.preventDefault()
            this.moveActiveOption(-1)
            break
          case 'Enter':
            e.preventDefault()
            if (this.activeOptionIndex >= 0) this.selectActiveOption()
            break
          case 'Escape':
            e.preventDefault()
            this.closeDropdown()
            break
        }
      },
    },
  })
</script>
<template>
  <div class="bh-select-wrapper" :style="{ paddingTop: label && !labelExistButNotVisible ? '20px' : '0' }">
    <Transition>
      <span
        v-if="label && labelIsVisible"
        :id="`${selectId}-label`"
        class="bh-label-tip"
        :class="{ 'sr-only': labelExistButNotVisible }"
      >
        {{ label }}
      </span>
    </Transition>
    <div
      class="select"
      :class="{
        active: isOpen,
        disabled,
        error: hasError,
        [getStyle]: getStyle,
      }"
    >
      <div
        :id="selectId"
        v-click-outside="closeDropdown"
        class="select-input"
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="isOpen"
        :aria-controls="listboxId"
        :aria-activedescendant="
          isOpen && activeOptionIndex >= 0 ? `${selectId}-option-${activeOptionIndex}` : undefined
        "
        :aria-labelledby="labelledById"
        :aria-label="ariaLabelComputed"
        :aria-describedby="describedByIds"
        :aria-invalid="hasError"
        :aria-disabled="disabled"
        :tabindex="disabled ? -1 : 0"
        @click="onToggleDropdown"
        @keydown="onKeydown"
        @focus="toggleLabel(true)"
        @blur="toggleLabel(false)"
      >
        <div
          class="select-input-inner-wrapper"
          :class="{ 'select-input-inner-wrapper-placeholder': placeholder && !internalValue }"
        >
          {{ getOptionTitle }}
        </div>
      </div>

      <transition>
        <div v-if="isOpen" class="select-dropdown" :style="`z-index:${zIndex}`">
          <div v-if="withSearch" class="select-search-wrapper">
            <input
              v-model="searchQuery"
              class="select-search-input"
              type="text"
              aria-label="Search options"
              :placeholder="searchPlaceholder"
              autocomplete="off"
              @click.stop
              @keydown="onSearchKeydown"
            />
          </div>
          <ul
            :id="listboxId"
            class="select-options"
            :class="{ 'select-options__max-content': isMaxContent }"
            role="listbox"
            :aria-labelledby="selectId"
          >
            <template v-for="(option, index) in filteredOptions" :key="`select-options-${index}-${option.title}`">
              <li v-if="option.isHeader" class="option-group-heading" role="presentation">
                {{ option.title }}
              </li>
              <li
                v-else
                :id="`${selectId}-option-${index}`"
                :class="{
                  active: option.selected,
                  'option-focused': index === activeOptionIndex,
                  'option--split': !!option.rightLabel,
                  'option--grouped': option.isGrouped,
                }"
                class="option"
                role="option"
                :aria-selected="option.value === modelValue"
                @click="onSelectOption(option.value)"
                @mouseenter="onOptionHover(index)"
              >
                <span class="option-label">{{ option.title }}</span>
                <span v-if="option.rightLabel" class="option-right">{{ option.rightLabel }}</span>
              </li>
            </template>
            <li v-if="withSearch && !hasSelectableOption" class="option option--empty">No search results</li>
          </ul>
        </div>
      </transition>
    </div>
    <Transition>
      <HasError
        v-if="hasError"
        :id="`${selectId}-error`"
        :key="`hasError-${selectId}`"
        :error="error"
        role="alert"
        aria-live="polite"
      />
    </Transition>
  </div>
</template>

<style scoped lang="scss">
  .select {
    font-family: var(--bf-font-body);
    position: relative;
    width: 100%;

    &-input {
      border: 1px solid var(--color-text-grey);
      border-radius: 6px;
      padding: 6px 10px;
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: relative;
      background-color: var(--color-background-grey-1);

      &::after {
        content: '';
        position: absolute;
        right: 20px;
        top: 8px;
        z-index: 3;
        border: 2px solid transparent;
        border-top: 2px solid var(--color-super-dark-grey);
        border-right: 2px solid var(--color-super-dark-grey);
        width: 7px;
        height: 7px;
        transform: rotate(135deg);
      }
    }

    .select-input-inner-wrapper-placeholder {
      color: var(--color-placeholder-grey);
    }

    &.disabled .select-input {
      border-color: var(--color-background-grey-3);
      color: var(--color-background-grey-3);
      cursor: not-allowed;

      &::after {
        border-top: 2px solid var(--color-background-grey-3);
        border-right: 2px solid var(--color-background-grey-3);
      }
    }

    &.error .select-input {
      border-color: var(--color-border-error);
      background-color: var(--color-background-error);
    }

    &.active {
      &.disabled .select-input::after {
        border-bottom: 2px solid var(--color-background-grey-3);
        border-left: 2px solid var(--color-background-grey-3);
      }

      & .select-input {
        border-color: var(--color-light-orange);

        &::after {
          border: 2px solid transparent;
          border-bottom: 2px solid #4c5664;
          border-left: 2px solid #4c5664;
          top: 13px;
        }
      }
    }

    &-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      background: var(--color-white);
      border-radius: 0 0 8px 8px;
      box-shadow: 0 32px 48px -8px #0000001a;
    }

    &-search-wrapper {
      padding: 6px 8px;
      border-bottom: 1px solid var(--color-background-grey-3);
    }

    &-search-input {
      width: 100%;
      border: 1px solid var(--color-text-grey);
      border-radius: 4px;
      padding: 5px 8px;
      font-size: 12px;
      font-family: var(--bf-font-body);
      background-color: var(--color-background-grey-1);
      outline: none;
      box-sizing: border-box;

      &:focus {
        border-color: var(--color-light-orange);
      }
    }

    &-options {
      width: 100%;
      display: flex;
      flex-direction: column;
      max-height: 200px;
      overflow-y: auto;
      list-style: none;
      margin: 0;
      padding: 0;

      & .option {
        padding: 10px;
        cursor: pointer;
        font-family: var(--bf-font-body);
        font-size: 15px;
        transition: background-color 0.3s;
        &:hover,
        &.option-focused {
          background-color: var(--color-grey-hover);
        }

        &[aria-selected='true'] {
          background-color: var(--color-light-orange);
          color: var(--color-white);
        }

        &--split {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .option-right {
          flex-shrink: 0;
          white-space: nowrap;
          color: var(--bf-dusky-1);
        }

        &--empty {
          color: var(--color-text-grey);
          cursor: default;
          font-size: 12px;

          &:hover {
            background-color: transparent;
          }
        }

        &--grouped {
          padding-left: 22px;
        }
      }

      & .option-group-heading {
        padding: 8px 10px 4px;
        font-family: var(--bf-font-display);
        font-size: 10px;
        font-weight: var(--bf-fw-bold);
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--bf-dusky-1);
        cursor: default;
      }

      & .option-group-heading:not(:first-child) {
        margin-top: 4px;
        border-top: 1px solid var(--bf-line-2);
      }

      &__max-content {
        max-height: max-content;
      }
    }
  }
  .active {
    background-color: var(--color-grey-hover);
    border-radius: unset !important;
  }
</style>
